import { motion } from 'framer-motion';
import { XMarkIcon, ClockIcon } from '@heroicons/react/24/outline';
import type { ConversionStatus } from '@types';

interface ConversionProgressProps {
  progress: number;
  status: ConversionStatus;
  statusMessage?: string;
  estimatedTimeRemaining?: number;
  onCancel?: () => void;
  showCancel?: boolean;
}

const STATUS_COLORS: Record<ConversionStatus, string> = {
  PENDING: 'bg-gray-500',
  PROCESSING: 'bg-blue-500',
  COMPLETED: 'bg-green-500',
  FAILED: 'bg-red-500',
  CANCELLED: 'bg-gray-500',
};

const STATUS_TEXT_COLORS: Record<ConversionStatus, string> = {
  PENDING: 'text-gray-700',
  PROCESSING: 'text-blue-700',
  COMPLETED: 'text-green-700',
  FAILED: 'text-red-700',
  CANCELLED: 'text-gray-700',
};

const STATUS_BG_COLORS: Record<ConversionStatus, string> = {
  PENDING: 'bg-gray-50',
  PROCESSING: 'bg-blue-50',
  COMPLETED: 'bg-green-50',
  FAILED: 'bg-red-50',
  CANCELLED: 'bg-gray-50',
};

const STATUS_LABELS: Record<ConversionStatus, string> = {
  PENDING: 'Pending',
  PROCESSING: 'Processing',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
  CANCELLED: 'Cancelled',
};

export default function ConversionProgress({
  progress,
  status,
  statusMessage,
  estimatedTimeRemaining,
  onCancel,
  showCancel = true,
}: ConversionProgressProps) {
  const formatTime = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const canCancel =
    showCancel && onCancel && (status === 'PENDING' || status === 'PROCESSING');

  return (
    <div className={`rounded-lg border p-6 ${STATUS_BG_COLORS[status]}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div
            className={`flex items-center space-x-2 ${STATUS_TEXT_COLORS[status]}`}
          >
            <div className="relative">
              <motion.div
                className={`h-3 w-3 rounded-full ${STATUS_COLORS[status]}`}
                animate={
                  status === 'PROCESSING'
                    ? {
                        scale: [1, 1.2, 1],
                        opacity: [1, 0.7, 1],
                      }
                    : {}
                }
                transition={
                  status === 'PROCESSING'
                    ? {
                        duration: 1.5,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }
                    : {}
                }
              />
            </div>
            <span className="text-sm font-medium">{STATUS_LABELS[status]}</span>
          </div>
        </div>

        {canCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="p-1 rounded-full hover:bg-gray-200 transition-colors"
            title="Cancel conversion"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        )}
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Progress</span>
          <span className="text-sm font-medium text-gray-700">
            {Math.round(progress)}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
          <motion.div
            className={`h-2.5 rounded-full ${STATUS_COLORS[status]}`}
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>
      </div>

      {/* Status Message */}
      {statusMessage && (
        <div className="mb-3">
          <p className="text-sm text-gray-600">{statusMessage}</p>
        </div>
      )}

      {/* Estimated Time */}
      {estimatedTimeRemaining !== undefined &&
        estimatedTimeRemaining > 0 &&
        status === 'PROCESSING' && (
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <ClockIcon className="h-4 w-4" />
            <span>
              Estimated time remaining: {formatTime(estimatedTimeRemaining)}
            </span>
          </div>
        )}

      {/* Processing Animation */}
      {status === 'PROCESSING' && (
        <div className="mt-4 flex space-x-1">
          {[0, 1, 2].map(i => (
            <motion.div
              key={i}
              className="w-2 h-2 bg-blue-500 rounded-full"
              animate={{
                y: [0, -10, 0],
                opacity: [0.5, 1, 0.5],
              }}
              transition={{
                duration: 0.8,
                repeat: Infinity,
                delay: i * 0.2,
                ease: 'easeInOut',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
